import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon, ChevronDownIcon, CornerDownRightIcon } from 'lucide-react';
import { SOLUTIONS_MENU } from '../../data/solutionsMenu';
import { cn } from '../../src/lib/utils';
const itemClass = 'flex flex-col rounded-lg px-3 py-2.5 transition-colors hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime';

/** Desktop Solutions dropdown. Opens on click, hover or keyboard; closes on Escape / outside click. */
export function SolutionsMenu({
  active
}: {
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const panelId = useId();
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => setOpen(false), [pathname, hash]);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const openNow = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  };
  return <li ref={rootRef} className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon} onBlur={event => {
    if (!rootRef.current?.contains(event.relatedTarget as Node)) setOpen(false);
  }} data-id="element-139" data-mp="27gb2bj9ajocb">
      <button ref={buttonRef} type="button" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(value => !value)} onKeyDown={event => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        window.requestAnimationFrame(() => rootRef.current?.querySelector<HTMLAnchorElement>('[data-menu-link]')?.focus());
      }
    }} className={cn('relative inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-4 focus-visible:ring-offset-ink 2xl:text-sm', active || open ? 'text-text-primary' : 'text-text-secondary')} data-id="element-140" data-mp="py2v3nxk5a5">
        Solutions
        <ChevronDownIcon className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden="true" data-id="element-141" data-mp="2z9els9jliir6" />
        {active ? <span aria-hidden="true" className="absolute -bottom-2 left-0 right-0 h-0.5 rounded-full bg-lime" data-id="element-142" data-mp="2ro2i3k2wm4rl" /> : null}
      </button>

      <div id={panelId} hidden={!open} className="absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-5" data-id="element-143" data-mp="31ntang7wm9p1">
        <div className="rounded-2xl border border-hairline bg-panel p-3 shadow-[0_30px_80px_-30px_rgba(20,14,60,0.35)]" data-id="element-144" data-mp="2wprpmpqx40ax">
          <Link to="/solutions" data-menu-link className="flex items-center justify-between rounded-lg bg-surface px-3 py-2.5 text-sm font-semibold text-text-primary transition-colors hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime" data-id="element-145" data-mp="28zfc8zcwlbcy">
            All solutions
            <ArrowRightIcon className="h-4 w-4 text-lime" aria-hidden="true" data-id="element-146" data-mp="1q7ml7ahea07v" />
          </Link>
          <ul className="mt-2 grid grid-cols-2 gap-1" data-id="element-147" data-mp="1l8ibmiqk60h0">
            {SOLUTIONS_MENU.map(item => <li key={item.href} data-id="element-148" data-mp="oj7q1inozuvt">
                <Link to={item.href} data-menu-link className={itemClass} data-id="element-149" data-mp="og3g04njq4x8">
                  <span className="text-sm font-semibold text-text-primary" data-id="element-150" data-mp="skb5i41tvowi">{item.label}</span>
                  <span className="text-xs text-text-tertiary" data-id="element-151" data-mp="xi2yl1e7lh2a">{item.descriptor}</span>
                </Link>
                {item.child ? <Link to={item.child.href} data-menu-link className={cn(itemClass, 'ml-3 flex-row items-start gap-2 py-2')} data-id="element-152" data-mp="178o4zb04jpvr">
                    <CornerDownRightIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime" aria-hidden="true" data-id="element-153" data-mp="1c6zefkmibydg" />
                    <span className="flex flex-col" data-id="element-154" data-mp="1gaf8qab3tmmz">
                      <span className="text-[13px] font-semibold text-text-primary" data-id="element-155" data-mp="1g7j7od3ymhub">{item.child.label}</span>
                      <span className="text-xs text-text-tertiary" data-id="element-156" data-mp="3i0qr9yqlvlm0">{item.child.descriptor}</span>
                    </span>
                  </Link> : null}
              </li>)}
          </ul>
        </div>
      </div>
    </li>;
}

/** Mobile Solutions accordion with the same hierarchy as the desktop dropdown. */
export function MobileSolutionsAccordion({
  onNavigate,
  active
}: {
  onNavigate: () => void;
  active: boolean;
}) {
  const [open, setOpen] = useState(active);
  const panelId = useId();
  const linkClass = 'flex min-h-[48px] flex-col justify-center py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime';
  return <li className="sm:col-span-2" data-id="element-157" data-mp="3tgbtj5m9o78w">
      <button type="button" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(value => !value)} className={cn('flex min-h-[48px] w-full items-center justify-between border-b border-hairline-soft text-left text-[15px] font-medium transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime', active ? 'text-lime' : 'text-text-secondary')} data-id="element-158" data-mp="3th3nkc07twx2">
        Solutions
        <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden="true" data-id="element-159" data-mp="2afb7k239nieu" />
      </button>
      <div id={panelId} hidden={!open} className="border-b border-hairline-soft pb-2" data-id="element-160" data-mp="20jgkmus0f169">
        <Link to="/solutions" onClick={onNavigate} className={cn(linkClass, 'pl-3 text-[15px] font-semibold text-text-primary')} data-id="element-161" data-mp="25ib31vm2enpu">
          All solutions
        </Link>
        <ul className="grid sm:grid-cols-2 sm:gap-x-6" data-id="element-162" data-mp="1rplepkj2d6pk">
          {SOLUTIONS_MENU.map(item => <li key={item.href} className="pl-3" data-id="element-163" data-mp="1wixt71u13x4l">
              <Link to={item.href} onClick={onNavigate} className={linkClass} data-id="element-164" data-mp="1ht8lsovcewvv">
                <span className="text-[15px] font-semibold text-text-primary" data-id="element-165" data-mp="1mrtfs6k04kbv">{item.label}</span>
                <span className="text-[13px] text-text-tertiary" data-id="element-166" data-mp="18s0ni6sttw1s">{item.descriptor}</span>
              </Link>
              {item.child ? <Link to={item.child.href} onClick={onNavigate} className={cn(linkClass, 'flex-row items-center justify-start gap-2 pl-4')} data-id="element-167" data-mp="1cskpbnochnu9">
                  <CornerDownRightIcon className="h-3.5 w-3.5 shrink-0 text-lime" aria-hidden="true" data-id="element-168" data-mp="u76f610fw77x" />
                  <span className="text-[14px] font-semibold text-text-primary" data-id="element-169" data-mp="p8lek0h9zliy">{item.child.label}</span>
                </Link> : null}
            </li>)}
        </ul>
      </div>
    </li>;
}